import { parseSSE } from "@sagot/chat";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { useApi } from "../../../session/session-provider";
import { useWorkspaceId } from "../../../session/workspace-context";
import { parseSourceEvent, type SourceEvent } from "./ingestion";
import { dkKeys } from "./keys";
import type { SourceView } from "./types";

const SOURCE_EVENT = "source";
const RECONNECT_BASE_MS = 2_000;
const RECONNECT_MAX_MS = 30_000;

export type SourceEventsState = "connecting" | "live" | "polling";

const RUN_STATUS: Readonly<Record<string, SourceView["status"]>> = { running: "processing", failed: "failed" };

/** The sync run's counters from an event, merged into the cached run (events never invent a run). */
function applyRun(sync: SourceView["sync"], run: NonNullable<SourceEvent["run"]>): SourceView["sync"] {
  if (!sync) return sync;
  return { ...sync, state: run.state, pages_total: run.pages_total, pages_done: run.pages_done, pages_failed: run.pages_failed };
}

/**
 * Applies one event to a cached source row. With a `run` snapshot the source is whatever the
 * run is (a page finishing or failing never makes the source ready/failed on its own);
 * events without one (older API) describe the source directly.
 */
export function applySourceEvent(source: SourceView, event: SourceEvent): SourceView {
  if (source.id !== event.source_id) return source;
  if (event.run) {
    const status = RUN_STATUS[event.run.state] ?? "ready";
    return {
      ...source,
      status,
      stage: event.stage,
      progress: event.progress,
      sync: applyRun(source.sync, event.run),
      status_reason: status === "failed" ? (event.error ?? source.status_reason) : status === "processing" ? null : source.status_reason,
    };
  }
  const status = event.status === "ready" ? "ready" : event.status === "failed" ? "failed" : "processing";
  return {
    ...source,
    status,
    stage: event.stage,
    progress: event.progress,
    status_reason: event.status === "failed" ? (event.error ?? source.status_reason) : source.status_reason,
  };
}

/** The source as a whole finished (its run closed); one page finishing or failing is not that. */
function isTerminal(event: SourceEvent): boolean {
  if (event.run) return event.run.state !== "running";
  return event.status === "ready" || event.status === "failed";
}

function useApplyToCache() {
  const queryClient = useQueryClient();
  const wsId = useWorkspaceId();
  return (event: SourceEvent) => {
    queryClient.setQueryData<SourceView[]>(dkKeys.sources(wsId), (rows) => rows?.map((row) => applySourceEvent(row, event)));
    queryClient.setQueryData<SourceView>(dkKeys.source(wsId, event.source_id), (row) => (row ? applySourceEvent(row, event) : row));
    if (isTerminal(event)) {
      void queryClient.invalidateQueries({ queryKey: dkKeys.source(wsId, event.source_id) });
      void queryClient.invalidateQueries({ queryKey: dkKeys.sources(wsId), exact: true });
    }
  };
}

/**
 * Live source status over SSE-over-fetch (`GET /v1/sources/events`, bearer auth, CLAUDE.md
 * rule 8). Reconnects with backoff; while disconnected the state is `polling` so callers can
 * turn on a `refetchInterval` fallback.
 */
export function useSourceEvents(onEvent?: (event: SourceEvent) => void): SourceEventsState {
  const api = useApi();
  const apply = useApplyToCache();
  const [state, setState] = useState<SourceEventsState>("connecting");
  const handlers = useRef({ apply, onEvent });
  useEffect(() => {
    handlers.current = { apply, onEvent };
  });

  useEffect(() => {
    const controller = new AbortController();
    let attempt = 0;
    const aborted = () => controller.signal.aborted;
    const wait = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));
    const run = async () => {
      while (!aborted()) {
        try {
          const { data, response } = await api.GET("/v1/sources/events", { parseAs: "stream", signal: controller.signal });
          if (!response.ok || !data) throw new Error("source events unavailable");
          setState("live");
          attempt = 0;
          for await (const message of parseSSE(data, { signal: controller.signal })) {
            if (message.event !== SOURCE_EVENT) continue;
            const event = parseSourceEvent(message.data);
            if (!event) continue;
            handlers.current.apply(event);
            handlers.current.onEvent?.(event);
          }
        } catch {
          // Network drop, 401 after refresh, or abort: fall back to polling until we reconnect.
        }
        if (aborted()) return;
        setState("polling");
        attempt += 1;
        await wait(Math.min(RECONNECT_BASE_MS * 2 ** attempt, RECONNECT_MAX_MS));
      }
    };
    void run();
    return () => {
      controller.abort();
    };
  }, [api]);

  return state;
}
